import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '@/components/base/Badge';
import Button from '@/components/base/Button';
import Loading from '@/components/base/Loading';
import ErrorState from '@/components/base/ErrorState';
import EmptyState from '@/components/base/EmptyState';
import Pagination from '@/components/base/Pagination';
import SortableTh from '@/components/base/SortableTh';
import ProjectToolbar from '@/pages/dashboard/components/ProjectToolbar';
import ProjectDeleteDialog from './components/ProjectDeleteDialog';
import { useTableControls } from '@/hooks/useTableControls';
import { useProjects } from '@/hooks/useProjects';
import { useProjectReceiptDates } from '@/hooks/useReceipts';
import { useAuth } from '@/contexts/AuthContext';
import { STATUS_CONFIG, STATUS_FILTERS, type StatusFilter } from '@/lib/projectStatus';
import { getProjectAlerts } from '@/lib/alerts';
import { formatDate } from '@/lib/format';
import type { Project } from '@/types';

export default function ProjectsPage() {
  const navigate = useNavigate();
  const { isSuperUsuario } = useAuth();
  const { projects, loading, error, reload } = useProjects();
  const { receiptDates } = useProjectReceiptDates();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('TODOS');
  const [toDelete, setToDelete] = useState<Project | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return projects.filter((project) => {
      const matchStatus = status === 'TODOS' || project.status === status;
      const matchTerm =
        !term ||
        project.name.toLowerCase().includes(term) ||
        (project.clients?.name || '').toLowerCase().includes(term);
      return matchStatus && matchTerm;
    });
  }, [projects, search, status]);

  const getSortValue = useCallback((project: Project, key: string): string | number => {
    switch (key) {
      case 'name':
        return project.name.toLowerCase();
      case 'client':
        return (project.clients?.name || '').toLowerCase();
      case 'status':
        return project.status;
      case 'development_start':
        return project.development_start ? new Date(project.development_start).getTime() : 0;
      case 'development_end':
        return project.development_end ? new Date(project.development_end).getTime() : 0;
      case 'billing': {
        const next = getProjectAlerts(project).nextBillingDate;
        return next ? next.getTime() : 0;
      }
      case 'contract_end':
        return project.contract_end ? new Date(project.contract_end).getTime() : 0;
      default:
        return '';
    }
  }, []);

  const table = useTableControls(filtered, getSortValue, {
    pageSize: 10,
    initialKey: 'name',
    initialDirection: 'asc',
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-foreground-950">Listado de proyectos</h2>
          <p className="text-sm text-foreground-500">
            {filtered.length} de {projects.length} proyectos
          </p>
        </div>
        <Button icon="ri-add-line" onClick={() => navigate('/proyectos/nuevo')}>
          Nuevo proyecto
        </Button>
      </div>

      <ProjectToolbar
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
      />

      {loading ? (
        <div className="rounded-lg border border-background-200 bg-background-50">
          <Loading label="Cargando proyectos..." />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="ri-folders-line"
          title={projects.length === 0 ? 'No hay proyectos registrados' : 'Sin resultados'}
          description={
            projects.length === 0
              ? 'Registre su primer proyecto para comenzar el seguimiento.'
              : 'Ajuste los filtros de búsqueda o el estado seleccionado.'
          }
          action={
            projects.length === 0 ? (
              <Button icon="ri-add-line" onClick={() => navigate('/proyectos/nuevo')}>
                Crear primer proyecto
              </Button>
            ) : (
              <Button
                variant="outline"
                icon="ri-refresh-line"
                onClick={() => {
                  setSearch('');
                  setStatus(STATUS_FILTERS[0].value);
                }}
              >
                Limpiar filtros
              </Button>
            )
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1120px] border-collapse text-left">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/70">
                  <SortableTh label="Proyecto / Cliente" sortKey="name" activeKey={table.sortKey} direction={table.direction} onSort={table.toggleSort} />
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    Celular
                  </th>
                  <SortableTh label="Inicio des." sortKey="development_start" activeKey={table.sortKey} direction={table.direction} onSort={table.toggleSort} />
                  <SortableTh label="Fin des." sortKey="development_end" activeKey={table.sortKey} direction={table.direction} onSort={table.toggleSort} />
                  <SortableTh label="Próx. cobro" sortKey="billing" activeKey={table.sortKey} direction={table.direction} onSort={table.toggleSort} />
                  <SortableTh label="Fin contrato" sortKey="contract_end" activeKey={table.sortKey} direction={table.direction} onSort={table.toggleSort} />
                  <SortableTh label="Estado" sortKey="status" activeKey={table.sortKey} direction={table.direction} onSort={table.toggleSort} />
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {table.rows.map((project) => {
                  const config = STATUS_CONFIG[project.status];
                  const alerts = getProjectAlerts(project, new Date(), receiptDates[project.id]);
                  return (
                    <tr
                      key={project.id}
                      className="border-b border-background-200 last:border-0 transition-colors hover:bg-background-100/50"
                    >
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => navigate(`/proyectos/${project.id}`)}
                          className="flex cursor-pointer items-center gap-3 text-left"
                        >
                          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md border border-background-200 bg-background-100">
                            {project.logo_url ? (
                              <img
                                src={project.logo_url}
                                alt={`Logo de ${project.name}`}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span className="flex h-full w-full items-center justify-center text-foreground-400">
                                <i className="ri-folder-3-line"></i>
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-foreground-900">{project.name}</p>
                            <p className="truncate text-xs text-foreground-500">
                              {project.clients?.name || 'Cliente no asignado'}
                            </p>
                          </div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground-700">
                        {project.clients?.contact_phone || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground-700">
                        {formatDate(project.development_start)}
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground-700">
                        {formatDate(project.development_end)}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-foreground-800">{formatDate(alerts.nextBillingDate)}</p>
                        {alerts.billingState === 'pendiente' && (
                          <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-amber-700">
                            <i className="ri-alarm-warning-line text-sm"></i>
                            COBRO PENDIENTE
                          </span>
                        )}
                        {alerts.billingState === 'hoy' && (
                          <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-red-700">
                            <i className="ri-notification-3-line text-sm"></i>
                            SE COBRA HOY
                          </span>
                        )}
                        {alerts.billingState === 'pagado' && (
                          <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-primary-700">
                            <i className="ri-checkbox-circle-line text-sm"></i>
                            PAGADO
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-foreground-800">{formatDate(project.contract_end)}</p>
                        {alerts.contractState === 'por_finalizar' && (
                          <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-red-700">
                            <i className="ri-error-warning-line text-sm"></i>
                            Por finalizar
                          </span>
                        )}
                        {alerts.contractState === 'finalizado' && (
                          <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-red-700">
                            <i className="ri-close-circle-line text-sm"></i>
                            Finalizado
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={config.tone} icon={config.icon}>
                          {config.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => navigate(`/proyectos/${project.id}`)}
                            aria-label={`Ver ${project.name}`}
                            title="Ver detalle"
                            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
                          >
                            <i className="ri-eye-line text-base"></i>
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate(`/proyectos/${project.id}/editar`)}
                            aria-label={`Editar ${project.name}`}
                            title="Editar"
                            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-accent-300 hover:bg-accent-50 hover:text-accent-700"
                          >
                            <i className="ri-edit-line text-base"></i>
                          </button>
                          {isSuperUsuario && (
                            <button
                              type="button"
                              onClick={() => setToDelete(project)}
                              aria-label={`Eliminar ${project.name}`}
                              title="Eliminar"
                              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 text-red-600 transition-colors hover:border-red-300 hover:bg-red-50"
                            >
                              <i className="ri-delete-bin-6-line text-base"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            page={table.page}
            pageCount={table.pageCount}
            total={table.total}
            pageSize={table.pageSize}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        </div>
      )}

      <ProjectDeleteDialog
        project={toDelete}
        onClose={() => setToDelete(null)}
        onDeleted={() => {
          setToDelete(null);
          reload();
        }}
      />
    </div>
  );
}